// Online play: rooms with a four-letter code, the crew roster (and its spectators), and the traffic between the host's
// kitchen and its guests. No accounts: hosting makes a code and an invite link, and opening the
// link (or typing the code) joins. The host's browser runs the shift; guests send their buttons and
// their own chef's position, and get the kitchen back as snapshots (see sync.js).
// Rooms run on the Tower's shared room relay: its session layer keeps who is in the room, the stage, and the update
// stream, and its lobby kit makes the invite link. This file keeps the kitchen's own roster and messages.
import { input } from '../engine/input.js';
import { audio } from '../engine/audio.js';
import { writeSnapshot, applySnapshot, applyEvents } from './sync.js';
import { isBot, MAX_CREW, makeRoomForPerson, refillBots } from '../game/helper.js';

const RELAY = 'https://room-relay.hunterbobeck.workers.dev';
const GAME = 'flying-saucer';
const PRESSES = ['grab', 'use', 'dash', 'swap'];

// the relay's session layer and lobby kit, loaded once when a room first opens
let relayModules = null;
const loadRelay = () => (relayModules ||= Promise.all([import(RELAY + '/roomSession.js'), import(RELAY + '/roomLobby.js')]));

const randomId = (n, abc = 'abcdefghijklmnopqrstuvwxyz0123456789') => Array.from({ length: n }, () => abc[Math.floor(Math.random() * abc.length)]).join('');

/** This tab's identity in rooms (the same one the relay's session keeps): kept for the tab's life, so a dropped connection rejoins as the same player. */
function tabPeer() {
  try {
    let p = sessionStorage.getItem(GAME + '-peer');
    if (!p) {
      p = randomId(12);
      sessionStorage.setItem(GAME + '-peer', p);
    }
    return p;
  } catch (e) {
    return randomId(12);
  }
}

/** A room code from an invite link (#QKZT or ?room=QKZT), if there is one. */
export function codeFromUrl() {
  const m = (location.hash.slice(1) || new URLSearchParams(location.search).get('room') || '').toUpperCase().match(/^[A-Z]{4}$/);
  return m ? m[0] : null;
}

export class Online {
  constructor(app) {
    this.app = app;
    this.peer = tabPeer();
    this.role = null; // 'host' | 'guest'
    this.status = 'idle'; // idle | connecting | open | reconnecting | closed
    this.problem = null;
    this.crew = []; // [{peer, dev}] in chef order; dev is the device on that player's own browser
    // [{peer, dev}]: people in the room watching instead of cooking (they still own bots and steer them with Delegate)
    this.spectators = [];
    this.hostPeer = null; // the host's own peer id (a guest learns it from the roster)
    this.away = new Set(); // peers whose connection dropped
    this.hostGone = false;
    this.stage = 'lobby'; // lobby | game | results (as the host tells it)
    this.onChange = null;
    // host: what each guest is pressing
    this.remote = new Map();
    // host: each guest's Delegate choices, 'peer:dev' -> {task: on} (kept for the whole session)
    this.prefs = {};
    // guest: press counters per local device, the latest kitchen from the host
    this.counts = {};
    this.pending = null;
    this.events = [];
    // host: people who arrived mid-shift, sent the shift once the roster has gone out
    this._sendStart = new Set();
    // the relay's session in this room, and its lobby kit, once loaded
    this.session = null;
    this.lobbyKit = null;
    this._game = null;
    this._closing = false;
  }
  get isHost() {
    return this.role === 'host';
  }
  get code() {
    return this.session ? this.session.code : null;
  }
  get inviteLink() {
    return this.lobbyKit && this.code ? this.lobbyKit.makeInviteLink(this.code) : '';
  }
  /** Players on this browser, as their device ids. */
  get localDevs() {
    return this.crew.filter((p) => p.peer === this.peer && !isBot(p)).map((p) => p.dev);
  }

  // ------------------------------------------------------------ connection
  host() {
    this.role = 'host';
    this.hostPeer = this.peer;
    this.crew = [{ peer: this.peer, dev: 'any' }];
    this.spectators = [];
    // the host's usual bot helpers come along (friends who join a full room take a bot's place)
    for (let i = 1; i <= Math.min(MAX_CREW - 1, this.app.save.bots || 0); i++) this.crew.push({ peer: this.peer, dev: 'bot' + i });
    // and a host who last watched a crew of bots watches again
    if (this.app.save.spectate) this._toSpectators(this.peer, 'any');
    this.stage = 'lobby';
    this._open(null);
  }
  join(code) {
    this.role = 'guest';
    this.crew = [];
    this.spectators = [];
    this._open(code.toUpperCase());
  }
  leave() {
    this._closing = true;
    if (this.session) this.session.leave();
    this.status = 'closed';
  }
  /** Loads the relay's session layer, then opens the room through it; the roster it shares is the kitchen's crew and spectators. */
  async _open(code) {
    this._closing = false;
    this.status = 'connecting';
    this.problem = null;
    this._changed();
    let modules;
    try {
      modules = await loadRelay();
    } catch (e) {
      relayModules = null;
      return this._fail('lost');
    }
    if (this._closing) return;
    const [sessionModule, lobbyKit] = modules;
    this.lobbyKit = lobbyKit;
    this.session = new sessionModule.RoomSession({
      game: GAME,
      readInput: () => this._readInput(),
      writeSnapshot: () => (this._game ? writeSnapshot(this._game) : null),
      onArrive: (peer) => this._arrive(peer),
      onDepart: (peer) => this._depart(peer),
      onShared: () => this._shared(),
      onSnapshot: (s, e) => {
        this.pending = s;
        if (e.length) this.events.push(...e);
      },
      onInput: (peer, m) => this._input(peer, m),
      onMessage: (from, m) => (this.isHost ? this._fromGuest(from, m) : this._fromHost(m)),
      onStatus: (status, problem) => this._status(status, problem),
    });
    const online = this;
    this.session.lobby = {
      get crew() {
        return online.crew;
      },
      get spectators() {
        return online.spectators;
      },
    };
    if (this.isHost) this.session.host();
    else this.session.join(code);
  }
  _status(status, problem) {
    if (status === 'host-gone') this.hostGone = true;
    else if (status === 'host-back') this.hostGone = false;
    else if (status === 'closed' && problem && !this._closing) return this._fail(problem);
    else this.status = status;
    this._changed();
  }
  _fail(why) {
    this.problem = why;
    this.leave();
    this._changed();
    this.app.onlineEnded(why);
  }
  _send(to, obj) {
    if (!this.session) return;
    if (to === '*') this.session.sendToEveryone(obj);
    else if (to === 'h') this.session.sendToHost(obj);
    else this.session.sendToPeer(to, obj);
  }
  broadcast(obj) {
    this._send('*', obj);
  }
  _changed() {
    if (this.onChange) this.onChange();
  }
  /** The session shared the room: a guest mirrors the host's roster, and the host sends the shift to anyone who arrived mid-shift. */
  _shared() {
    const s = this.session;
    this.stage = s.stage;
    this.away = s.away;
    if (this.isHost) {
      const g = this.app.game;
      for (const peer of this._sendStart) if (this.stage === 'game' && g) this._send(peer, { t: 'start', level: g.levelIndex, infinite: g.infinite, crew: this.crew, spectators: this.spectators });
      this._sendStart.clear();
    } else {
      this.crew = s.lobby.crew || [];
      this.spectators = s.lobby.spectators || [];
      this.hostPeer = s.hostPeer;
    }
    this._changed();
  }

  // ------------------------------------------------------------ host side
  /** Host: someone arrived (or came back): they keep their place, take a bot's, take a chef of their own mid-shift, or watch. */
  _arrive(peer) {
    if (peer === this.peer) return { peer };
    this.away.delete(peer);
    // someone coming back (a dropped connection) keeps their place, cooking or watching
    const known = this.crew.some((p) => p.peer === peer) || this.spectators.some((p) => p.peer === peer);
    // a full lobby: a person takes a bot's place
    if (!known && this.stage !== 'game') makeRoomForPerson(this.crew);
    if (!known) {
      const g = this.stage === 'game' && this.app.game;
      // a chef of their own (mid-shift, maybe a bot's); with four people already cooking, they watch
      if (g && g.seatPeer(peer, 'any')) this.crew = g.players.map((p) => ({ peer: p.peer, dev: p.dev }));
      else if (!g && this.crew.length < MAX_CREW) this.crew.push({ peer, dev: 'any' });
      else {
        this.spectators.push({ peer, dev: 'any' });
        if (g) g.addSpectator(peer, 'any');
      }
    }
    this._refillBots();
    // someone arriving mid-shift jumps straight into the kitchen, once the roster has gone out
    if (this.stage === 'game') this._sendStart.add(peer);
    audio.sfx('guestArrive');
    return { peer };
  }
  /** Host: someone left. Outside a shift their seats go too; during a shift they stay listed, as away. */
  _depart(peer) {
    this.remote.delete(peer);
    if (this.stage !== 'game') this._dropPeer(peer);
    this._refillBots();
    return this.stage === 'game';
  }
  /** Host: what a guest is pressing, and where their own chefs are. */
  _input(peer, m) {
    const r = this.remote.get(peer) || { held: {}, counts: {}, seen: {} };
    this.remote.set(peer, r);
    for (const dev in m.d) {
      const d = m.d[dev];
      r.held[dev] = d;
      const seen = (r.seen[dev] = r.seen[dev] || {});
      for (const k of PRESSES) {
        // a reloaded page starts counting again from zero
        if (seen[k] == null || d[k] < seen[k]) seen[k] = d[k];
      }
      r.counts[dev] = d;
    }
    if (this.app.game && m.c) this.app.game.applyReports(peer, m.c);
  }
  _fromGuest(peer, m) {
    switch (m.t) {
      case 'add':
        if (this.stage === 'lobby' && this.crew.length < MAX_CREW && !this.crew.some((p) => p.peer === peer && p.dev === m.dev)) this.crew.push({ peer, dev: m.dev });
        this._sendRoster();
        break;
      case 'dg':
        // a guest switched a Delegate task on or off, for their own bots
        if (this.app.game && typeof m.id === 'string' && typeof m.dev === 'string') this.app.game.toggleDelegate(peer + ':' + m.dev, m.id, !!m.on);
        break;
      case 'dgp': {
        // a guest's Delegate choices, from their own save
        if (typeof m.dev !== 'string' || !m.prefs || typeof m.prefs !== 'object') break;
        const key = peer + ':' + m.dev, clean = {};
        for (const k in m.prefs) if (typeof m.prefs[k] === 'boolean') clean[k] = m.prefs[k];
        this.prefs[key] = clean;
        if (this.app.game) Object.assign(this.app.game.prefsOf(key), clean);
        break;
      }
      case 'drop':
        // (a crew always keeps at least one chef, bot or person)
        if (this.stage === 'lobby' && this.crew.length > 1) this.crew = this.crew.filter((p) => !(p.peer === peer && p.dev === m.dev));
        this._sendRoster();
        break;
      case 'watch':
        // a guest's person steps out of the crew to watch
        if (this.stage === 'lobby' && typeof m.dev === 'string') this._toSpectators(peer, m.dev);
        this._sendRoster();
        break;
      case 'cook':
        // a guest's spectator takes a chef again
        if (this.stage === 'lobby' && typeof m.dev === 'string') this._toCrew(peer, m.dev);
        this._sendRoster();
        break;
      case 'pause':
        if (m.on) this.app.pause(true);
        else this.app.resume(true);
        break;
    }
    this._changed();
  }
  _dropPeer(peer) {
    const stays = (p) => p.peer !== peer || p.peer === this.peer;
    this.crew = this.crew.filter(stays);
    this.spectators = this.spectators.filter(stays);
    this.remote.delete(peer);
  }
  /** Host: a person's chef leaves the crew and they watch instead. False when theirs is the last chef: a crew always keeps one. */
  _toSpectators(peer, dev) {
    const i = this.crew.findIndex((p) => p.peer === peer && p.dev === dev);
    if (i < 0 || this.crew.length <= 1) return false;
    this.crew.splice(i, 1);
    this.spectators.push({ peer, dev });
    return true;
  }
  /** Host: a spectator takes a chef again, in an open slot or (as someone joining does) in a full crew's last bot's place. */
  _toCrew(peer, dev) {
    const i = this.spectators.findIndex((p) => p.peer === peer && p.dev === dev);
    if (i < 0 || !makeRoomForPerson(this.crew)) return false;
    this.spectators.splice(i, 1);
    this.crew.push({ peer, dev });
    return true;
  }
  /** In the lobby, seats that reopened (someone stepped out or left) go back to the host's bot helpers. */
  _refillBots() {
    if (this.isHost && this.stage === 'lobby') refillBots(this.crew, this.app.save.bots || 0, (dev) => ({ peer: this.peer, dev }));
  }
  /** Host: tell everyone the roster (the session sends it with the stage and who is away). */
  _sendRoster() {
    this._refillBots();
    if (this.isHost && this.session) this.session.share();
    else this._changed();
  }
  /** A remote player's controller this frame; each press is handed out exactly once. */
  ctrlFor(device) {
    const [, peer, dev] = device.split(':');
    const out = { mx: 0, mz: 0, grab: false, use: false, useHeld: false, dash: false, swap: false };
    const r = this.remote.get(peer);
    const d = r && r.held[dev];
    if (!d || this.away.has(peer)) return out;
    out.mx = d.mx;
    out.mz = d.mz;
    out.useHeld = !!d.uh;
    const seen = r.seen[dev];
    for (const k of PRESSES) {
      if (d[k] > seen[k]) {
        seen[k]++;
        out[k] = true;
      }
    }
    return out;
  }
  /** The host picked a shift (timed or infinite): everyone goes in together. */
  startShift(levelIndex, infinite = false) {
    this.stage = 'game';
    const here = (p) => p.peer === this.peer || !this.away.has(p.peer);
    this.crew = this.crew.filter(here);
    this.spectators = this.spectators.filter(here);
    this.session.setStage('game');
    this.broadcast({ t: 'start', level: levelIndex, infinite, crew: this.crew, spectators: this.spectators });
  }
  shiftEnded(results) {
    this.stage = 'results';
    this.session.setStage('results');
    this.broadcast({ t: 'end', results });
  }
  backToLobby() {
    this.stage = 'lobby';
    for (const peer of this.away) this._dropPeer(peer);
    this._refillBots();
    this.session.setStage('lobby');
    this.broadcast({ t: 'lobby' });
  }
  setPaused(on) {
    if (this.isHost) this.broadcast({ t: 'pause', on });
    else this._send('h', { t: 'pause', on });
  }

  // ------------------------------------------------------------ guest side
  _fromHost(m) {
    switch (m.t) {
      case 'start':
        this.crew = m.crew;
        this.spectators = m.spectators || [];
        this.stage = 'game';
        this.pending = null;
        this.events = [];
        this.app.startLevel(m.level, { fromHost: true, infinite: !!m.infinite });
        break;
      case 'end':
        this.stage = 'results';
        this.app.finishLevel(m.results);
        break;
      case 'lobby':
        this.stage = 'lobby';
        this.app.toOnlineLobby();
        break;
      case 'pause':
        if (m.on) this.app.pause(true);
        else this.app.resume(true);
        break;
    }
    this._changed();
  }
  /** Ask the host for another player seat for a device on this browser (couch + online). */
  /** Host: a bot helper joins the crew (it counts as a player, for goals too). */
  addBot() {
    if (!this.isHost || this.stage !== 'lobby' || this.crew.length >= MAX_CREW) return;
    let n = 1;
    while (this.crew.some((p) => p.dev === 'bot' + n)) n++;
    this.crew.push({ peer: this.peer, dev: 'bot' + n });
    this.app.saveBots(this.crew.filter(isBot).length);
    this._sendRoster();
  }
  /** This browser's person steps out of the crew to watch (the host decides; a guest asks the host). */
  spectate(dev) {
    if (!this.isHost) return this._send('h', { t: 'watch', dev });
    if (this.stage === 'lobby' && this._toSpectators(this.peer, dev) && dev === 'any') this.app.saveSpectating(true);
    this._sendRoster();
  }
  /** This browser's spectator takes a chef again. */
  cook(dev) {
    if (!this.isHost) return this._send('h', { t: 'cook', dev });
    if (this.stage === 'lobby' && this._toCrew(this.peer, dev) && dev === 'any') this.app.saveSpectating(false);
    this._sendRoster();
  }
  /** Guest: switch a Delegate task on or off for this player's bots (the host runs the bots). */
  delegate(dev, id, on) {
    this._send('h', { t: 'dg', dev, id, on });
  }
  /** Guest: this player's Delegate choices, so the host can set their bots going. */
  sendPrefs(dev, prefs) {
    this._send('h', { t: 'dgp', dev, prefs });
  }
  addLocal(dev) {
    if (this.isHost) {
      if (this.stage === 'lobby' && this.crew.length < MAX_CREW) this.crew.push({ peer: this.peer, dev });
      this._sendRoster();
    } else this._send('h', { t: 'add', dev });
  }
  dropLocal(dev) {
    if (this.isHost) {
      // (a crew always keeps at least one chef, bot or person)
      if (this.crew.length > 1) this.crew = this.crew.filter((p) => !(p.peer === this.peer && p.dev === dev));
      if (dev.startsWith('bot')) this.app.saveBots(this.crew.filter(isBot).length);
      this._sendRoster();
    } else this._send('h', { t: 'drop', dev });
  }
  /** Read a device on this browser for our own chef, counting presses for the host. */
  readLocal(dev) {
    const claimed = this.localDevs.filter((d) => d !== 'any');
    const s = dev === 'any' ? input.readAny(claimed) : input.read(dev);
    const c = (this.counts[dev] = this.counts[dev] || { grab: 0, use: 0, dash: 0, swap: 0, mx: 0, mz: 0, uh: 0 });
    for (const k of PRESSES) if (s[k]) c[k]++;
    c.mx = Math.round(s.mx * 100) / 100;
    c.mz = Math.round(s.mz * 100) / 100;
    c.uh = s.useHeld ? 1 : 0;
    return s;
  }

  // ------------------------------------------------------------ per frame
  /** Called every frame while a shift is on screen: the session sends the kitchen (host) or this browser's buttons (guest) on its schedule. */
  tick(game, dt) {
    if (!this.session) return;
    this._game = game;
    if (this.isHost) this.session.queueEvents(game.takeEvents());
    this.session.tick(dt);
  }
  /** Guest: this browser's press counters and its own chefs' positions, for the host. */
  _readInput() {
    const game = this._game;
    const r2 = (v) => Math.round(v * 100) / 100;
    const mine = game ? game.chefs.filter((c) => c.local && !c.netLock) : [];
    return { d: this.counts, c: mine.map((c) => [c.index, r2(c.x), r2(c.z), r2(c.face), r2(c.vx), r2(c.vz), r2(c.dashT), c.warp]) };
  }
  /** Guest: bring the mirrored kitchen up to the latest snapshot, then play its effects. */
  applyPending(game) {
    if (this.pending) {
      applySnapshot(game, this.pending);
      this.pending = null;
    }
    if (this.events.length) {
      const ev = this.events;
      this.events = [];
      applyEvents(game, ev, this.peer);
    }
  }
}